import { toValue, watch, type MaybeRefOrGetter } from 'vue';
import { isApplePlatform, parseCombo, type NxKeyCombo } from '../Shortcuts/keys';
import { onDispose } from './element';

/** Input types that take no typing, so shortcuts stay live while they have focus. */
const NON_TEXT_INPUTS = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

const EDITABLE_ROLES =
  '[role="textbox"], [role="searchbox"], [role="combobox"], [role="spinbutton"]';

/**
 * Whether a key press lands where the reader is typing: a text input, a
 * textarea, a select, anything `contenteditable`, an ARIA text widget or a
 * CodeMirror editor. Single-key shortcuts must stand back there. Accepts the
 * event (reads through shadow roots) or the element.
 */
export function isEditableTarget(source: Event | EventTarget | null | undefined): boolean {
  if (!source) return false;
  let target: unknown = source;
  if (typeof Event !== 'undefined' && source instanceof Event) {
    const path = typeof source.composedPath === 'function' ? source.composedPath() : [];
    target = path[0] ?? source.target;
  }
  if (typeof Element === 'undefined' || !(target instanceof Element)) return false;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(target.type);
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  const editable = target.closest('[contenteditable]');
  if (editable && editable.getAttribute('contenteditable') !== 'false') return true;
  return !!target.closest(`${EDITABLE_ROLES}, .cm-editor`);
}

const KEY_ALIASES: Record<string, string> = {
  esc: 'escape',
  return: 'enter',
  del: 'delete',
  space: ' ',
  spacebar: ' ',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  plus: '+',
};

type Modifier = 'ctrl' | 'meta' | 'alt' | 'shift';

const MODIFIERS: Record<string, Modifier | 'mod'> = {
  mod: 'mod',
  ctrl: 'ctrl',
  control: 'ctrl',
  strg: 'ctrl',
  meta: 'meta',
  cmd: 'meta',
  command: 'meta',
  win: 'meta',
  alt: 'alt',
  option: 'alt',
  opt: 'alt',
  shift: 'shift',
};

interface ParsedCombo {
  key: string;
  mods: Set<Modifier>;
  /** Whether a modifier that doesn't type (Ctrl, ⌘, Mod) is part of it. */
  command: boolean;
}

function parse(combo: NxKeyCombo | string, apple: boolean): ParsedCombo {
  const mods = new Set<Modifier>();
  let key = '';
  let command = false;
  // parseCombo splits at every `+`, so „Ctrl++“ would lose its key.
  const parts =
    typeof combo === 'string' && /.\s*\+\s*\+\s*$/.test(combo)
      ? [...parseCombo(combo.replace(/\s*\+\s*\+\s*$/, '')), '+']
      : parseCombo(combo);
  for (const part of parts) {
    const id = part.toLowerCase();
    const mod = MODIFIERS[id];
    if (mod === 'mod') {
      mods.add(apple ? 'meta' : 'ctrl');
      command = true;
    } else if (mod) {
      mods.add(mod);
      if (mod === 'ctrl' || mod === 'meta') command = true;
    } else {
      key = KEY_ALIASES[id] ?? id;
    }
  }
  return { key, mods, command };
}

/** The key a physical `code` stands for on a US layout: `KeyK` → `k`. */
function keyFromCode(code: string): string {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase();
  if (/^Digit\d$/.test(code)) return code.slice(5);
  return '';
}

function matchParsed(event: KeyboardEvent, combo: ParsedCombo): boolean {
  if (!combo.key) return false;
  if (event.ctrlKey !== combo.mods.has('ctrl')) return false;
  if (event.metaKey !== combo.mods.has('meta')) return false;
  if (event.altKey !== combo.mods.has('alt')) return false;
  const pressed = (event.key ?? '').toLowerCase();
  const isLetter = /^\p{L}$/u.test(combo.key);
  // A symbol like `?` or `#` already needs Shift to be typed; the combo
  // names the symbol, not the finger work. Named keys and letters must match.
  const symbol = combo.key.length === 1 && !isLetter && !/^\d$/.test(combo.key);
  if (!symbol && event.shiftKey !== combo.mods.has('shift')) return false;
  if (pressed === combo.key) return true;
  // ⌥K on a Mac types „˚“ — fall back to the physical key when a modifier is held.
  if (event.altKey || event.ctrlKey || event.metaKey) {
    return keyFromCode(event.code ?? '') === combo.key;
  }
  return false;
}

/**
 * Whether a key event is `combo` („Mod+S“, `['Shift', 'Enter']`, „?“).
 * `Mod` is ⌘ on Apple platforms and Ctrl elsewhere. Modifiers must match
 * exactly — „S“ is not „Mod+S“ — except that Shift is implied by symbols it
 * produces: „?“ matches Shift+ß as well as a layout's own „?“ key.
 */
export function matchCombo(
  event: KeyboardEvent,
  combo: NxKeyCombo | string,
  apple = isApplePlatform()
): boolean {
  return matchParsed(event, parse(combo, apple));
}

export type NxHotkeyHandler = (event: KeyboardEvent) => void | boolean;

export interface NxHotkeyBinding {
  handler: NxHotkeyHandler;
  /** Fire inside text fields too (Mod+S to save a form). Default `false`. */
  allowWhenTyping?: boolean;
  /** Call `preventDefault()` when it fires. Default: the hook's setting. */
  preventDefault?: boolean;
}

export interface NxHotkeysOptions {
  /** Skip presses inside text fields (see {@link isEditableTarget}). Default `true`. */
  ignoreWhenTyping?: boolean;
  /** Where to listen. Default `window`. */
  target?: MaybeRefOrGetter<EventTarget | null | undefined>;
  /** Listen only while true. Default `true`. */
  enabled?: MaybeRefOrGetter<boolean>;
  /** Call `preventDefault()` on a hit. Default `true`; a handler returning `false` skips it. */
  preventDefault?: boolean;
  /** Time allowed between the steps of a sequence like „G I“. Default 1000 ms. */
  sequenceMs?: number;
}

export type NxHotkeyMap = Record<string, NxHotkeyHandler | NxHotkeyBinding>;

interface Entry {
  steps: ParsedCombo[];
  binding: NxHotkeyBinding;
}

/** „Mod + K“ stays one step; „G I“ (space-separated) is a sequence of two. */
function splitSequence(combo: string): string[] {
  const normalised = combo.trim().replace(/\s*\+\s*/g, '+');
  if (normalised === '+' || normalised === ' ') return [normalised];
  return normalised.split(/\s+/).filter(Boolean);
}

/**
 * Keyboard shortcuts for a view: `{ 'Mod+K': openPalette, 'G I': goInbox,
 * '?': showHelp }`, or a single `useNxHotkeys('Escape', close)`. Letters and
 * other plain keys stay quiet while the reader types in a field unless a
 * binding sets `allowWhenTyping`. IME composition and presses something else
 * already handled (`defaultPrevented`) are skipped. The first matching
 * binding wins. Returns `stop`; also stops with the calling scope.
 */
export function useNxHotkeys(
  bindings: MaybeRefOrGetter<NxHotkeyMap>,
  options?: NxHotkeysOptions
): () => void;
export function useNxHotkeys(
  combo: string,
  handler: NxHotkeyHandler,
  options?: NxHotkeysOptions
): () => void;
export function useNxHotkeys(
  first: MaybeRefOrGetter<NxHotkeyMap> | string,
  second?: NxHotkeyHandler | NxHotkeysOptions,
  third?: NxHotkeysOptions
): () => void {
  const single = typeof first === 'string';
  const options: NxHotkeysOptions = (single ? third : (second as NxHotkeysOptions)) ?? {};
  const source: MaybeRefOrGetter<NxHotkeyMap> = single
    ? { [first]: second as NxHotkeyHandler }
    : first;
  const sequenceMs = options.sequenceMs ?? 1000;
  const apple = isApplePlatform();

  const entries = (): Entry[] =>
    Object.entries(toValue(source)).map(([combo, value]) => ({
      steps: splitSequence(combo).map((step) => parse(step, apple)),
      binding: typeof value === 'function' ? { handler: value } : value,
    }));

  // Steps already pressed of a sequence in progress.
  let pending: KeyboardEvent[] = [];
  let pendingAt = 0;
  // A lone key that also starts a longer sequence („G“ next to „G I“) waits
  // for the sequence window before it runs.
  let deferred: { run: () => void; timer: ReturnType<typeof setTimeout> } | null = null;
  const dropDeferred = () => {
    if (deferred) clearTimeout(deferred.timer);
    deferred = null;
  };
  const flushDeferred = () => {
    const run = deferred?.run;
    dropDeferred();
    run?.();
  };
  const prevents = (entry: Entry) => entry.binding.preventDefault ?? options.preventDefault ?? true;

  const onKeyDown = (raw: Event) => {
    const event = raw as KeyboardEvent;
    if (toValue(options.enabled) === false) return;
    if (event.isComposing || event.defaultPrevented) return;
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
    const typing = (options.ignoreWhenTyping ?? true) && isEditableTarget(event);
    if (pending.length && event.timeStamp - pendingAt > sequenceMs) pending = [];
    const history = [...pending, event];
    const list = entries().filter((e) => !typing || e.binding.allowWhenTyping);

    const matches = (entry: Entry, pressed: KeyboardEvent[]) =>
      entry.steps.length >= pressed.length &&
      pressed.every((ev, i) => matchParsed(ev, entry.steps[i]!));

    // Longest possible run first: with pending „G“, try „G I“ before a lone „I“.
    for (let start = 0; start < history.length; start++) {
      const pressed = history.slice(start);
      // The sequence did not go on: the waiting lone key runs first.
      if (start > 0) flushDeferred();
      const complete = list.find((e) => e.steps.length === pressed.length && matches(e, pressed));
      const longer = list.some((e) => e.steps.length > pressed.length && matches(e, pressed));
      if (complete && longer) {
        dropDeferred();
        if (prevents(complete)) event.preventDefault();
        pending = pressed;
        pendingAt = event.timeStamp;
        deferred = {
          run: () => {
            pending = [];
            complete.binding.handler(event);
          },
          timer: setTimeout(flushDeferred, sequenceMs),
        };
        return;
      }
      if (complete) {
        dropDeferred();
        pending = [];
        const result = complete.binding.handler(event);
        if (prevents(complete) && result !== false) event.preventDefault();
        return;
      }
      if (longer) {
        dropDeferred();
        pending = pressed;
        pendingAt = event.timeStamp;
        return;
      }
    }
    flushDeferred();
    pending = [];
  };

  let current: EventTarget | null = null;
  const attach = (next: EventTarget | null | undefined) => {
    current?.removeEventListener('keydown', onKeyDown);
    current = next ?? null;
    current?.addEventListener('keydown', onKeyDown);
  };
  const stopWatch = watch(
    () =>
      options.target === undefined
        ? typeof window === 'undefined'
          ? null
          : window
        : toValue(options.target),
    attach,
    { immediate: true }
  );
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    dropDeferred();
    stopWatch();
    attach(null);
  };
  onDispose(stop);
  return stop;
}
